// Settings — 5-category theme picker + Typography + WhatsApp-style sticker packs + custom uploads
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import api from './studentApi';
import { useStudent } from './StudentApp';
import {
  STICKER_PACKS, themesByTier, packsByTier, getTheme,
  recommendedTier, THEME_TIERS, FONT_OPTIONS, FONT_SCALES, DEFAULT_THEME_ID,
  emojiToTwemojiUrl,
} from './themes';
import { savedLookPrefs, hasUnsavedLookChanges, savedTargetExam, nextTargetExam } from './settingsChanges';
import { Check, Upload, Trash2, Plus } from 'lucide-react';
// Privacy & Personalisation (commented out): import { ShieldCheck, Brain } from 'lucide-react';
import { getExamPrepGoldGradient } from './examPrepTheme';
import { AccentRule } from './studentUi';
import { toast } from 'sonner';
import { notifyError } from '../../lib/errors';
import { confirmDialog } from '../../components/ConfirmDialog';

const EXAM_OPTIONS = [
  { value: 'JEE_MAIN', label: 'JEE Main' },
  { value: 'JEE_ADVANCED', label: 'JEE Advanced' },
  { value: 'NEET', label: 'NEET' },
  { value: 'SAT', label: 'SAT' },
  { value: 'ITO', label: 'ITO' },
];
const EXAM_LABELS = Object.fromEntries(EXAM_OPTIONS.map(o => [o.value, o.label]));

export default function StudentSettingsPage() {
  const { me, theme, refreshMe } = useStudent();
  const profile = me?.student_profile || {};
  const examGoldGradient = getExamPrepGoldGradient(theme?.id);

  const [themeId, setThemeId]     = useState(profile.theme_id || DEFAULT_THEME_ID);
  const [stickers, setStickers]   = useState(profile.stickers || []);
  const [accent, setAccent]       = useState(profile.accent_color || '');
  const [fontScale, setFontScale] = useState(profile.font_scale || 'md');
  const [fontOverride, setFontOverride] = useState(profile.font_override || '');
  const [saving, setSaving]       = useState(false);

  const initialTier = recommendedTier(profile.age_band) || 'standard';
  const [tierTab, setTierTab] = useState(initialTier);
  const [packTab, setPackTab] = useState('');
  const [customStickers, setCustomStickers] = useState([]);

  // Educational personalisation toggle (commented out) — it only gated an
  // unused next-lesson recommender; Magic Desk / tutor never read it.
  // Uncomment this block and the Privacy & Personalisation section below to restore.
  // // DPDP — educational personalisation toggle
  // const [personalisation, setPersonalisation] = useState(true);
  // const [personalisationLoading, setPersonalisationLoading] = useState(false);
  // useEffect(() => {
  //   (async () => {
  //     try {
  //       const res = await api.get('/preferences/personalisation');
  //       setPersonalisation(res.personalisation_enabled !== false);
  //     } catch { /* default true */ }
  //   })();
  // }, []);
  // const togglePersonalisation = async (next) => {
  //   setPersonalisationLoading(true);
  //   try {
  //     await api.post('/preferences/personalisation', { personalisation_enabled: next });
  //     setPersonalisation(next);
  //     toast.success(next ? 'Personalisation ON — your recommendations will adapt as you learn'
  //                         : 'Personalisation OFF — lessons will be shown chronologically');
  //   } catch {
  //     toast.error('Could not update preference');
  //   }
  //   setPersonalisationLoading(false);
  // };
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  // Exam Prep — which exam the student is preparing for. Tapping only picks
  // it; like the theme/font options it's saved with the "Save changes" button.
  const [targetExam, setTargetExam] = useState(profile.target_exam || '');

  useEffect(() => {
    setThemeId(profile.theme_id || DEFAULT_THEME_ID);
    setStickers(profile.stickers || []);
    setAccent(profile.accent_color || '');
    setFontScale(profile.font_scale || 'md');
    setFontOverride(profile.font_override || '');
    setTargetExam(profile.target_exam || '');
  }, [profile.theme_id, profile.stickers, profile.accent_color, profile.font_scale, profile.font_override, profile.target_exam]);

  useEffect(() => {
    const first = packsByTier(tierTab === 'standard' || tierTab === 'editorial' || tierTab === 'vibrant' || tierTab === 'dark' ? 'mature' : 'playful')[0];
    if (first && !packTab) setPackTab(first.id);
  }, [tierTab, packTab]);

  const loadCustom = async () => {
    try {
      const r = await api.get('/student/profile/stickers/custom');
      setCustomStickers(r.stickers || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadCustom(); }, []);

  const t = useMemo(() => getTheme(themeId), [themeId]);

  const toggleSticker = (id) => {
    setStickers(s => {
      if (s.includes(id)) return s.filter(x => x !== id);
      if (s.length >= 50) {
        toast.error('You can decorate with up to 50 stickers.');
        return s;
      }
      return [...s, id];
    });
  };

  const lookChanged = hasUnsavedLookChanges(savedLookPrefs(profile),
    { themeId, accent, fontScale, fontOverride, stickers });
  const examChanged = targetExam !== savedTargetExam(profile);
  const hasChanges = lookChanged || examChanged;

  const save = async () => {
    if (!hasChanges) return; // nothing changed — no PUT, no "Saved!" toast
    setSaving(true);
    try {
      if (examChanged) {
        await api.put('/student/profile/exam-target', { target_exam: targetExam || null });
      }
      if (lookChanged) {
        await api.put('/student/profile/theme', {
          theme_id: themeId,
          // Send '' (not null) when reset — the backend's `is not None` guard
          // treats an explicit JSON null as "field omitted" and skips the
          // update entirely, so a null here would leave the old custom color
          // in place. Empty string still clears it (see font_override below,
          // which already uses this same pattern).
          accent_color: accent || '',
          stickers,
          font_scale: fontScale,
          font_override: fontOverride || '',
        });
      }
      toast.success(lookChanged ? 'Saved! Your dashboard is updated.'
        : targetExam ? `Saved — target exam set to ${EXAM_LABELS[targetExam]}` : 'Saved — target exam cleared');
      await refreshMe();
    } catch (e) { notifyError(e, 'Failed'); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/webp', 'image/jpeg', 'image/jpg', 'image/gif'].includes(file.type)) {
      toast.error('Use PNG, WEBP, JPEG or GIF.');
      e.target.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Max 2 MB.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', file.name.replace(/\.[^.]+$/, '').slice(0, 24));
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student/profile/stickers/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('desky_token')}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      toast.success('Sticker uploaded');
      await loadCustom();
    } catch (e) { notifyError(e, 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const deleteCustom = async (id) => {
    if (!(await confirmDialog({ title: 'Delete sticker', message: 'Delete this sticker?', danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await api.del(`/student/profile/stickers/custom/${id}`);
      setStickers(s => s.filter(x => x !== id));
      await loadCustom();
      toast.success('Sticker removed.');
    } catch (e) { notifyError(e, 'Failed'); }
  };

  // Sticker pack pills are grouped: Universal / Mature / Playful (sticker tier) — independent of theme tier
  const STICKER_TIER_GROUPS = [
    { id: 'universal', label: 'Universal', desc: 'Available to everyone' },
    { id: 'mature',    label: 'Pro',       desc: 'Refined, study-friendly' },
    { id: 'playful',   label: 'Playful',   desc: 'Fun & cartoony' },
  ];

  const activePack = STICKER_PACKS.find(p => p.id === packTab);

  return (
    <div className="space-y-10 pb-32" data-testid="student-settings-page">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--st-mute)' }}>Change how your desk looks. Press Save to keep your changes</p>
      </header>

      {/* Account */}
      <section data-testid="settings-account">
        <h2 className="text-xl font-semibold">Account</h2><AccentRule className="mt-2 mb-4" />
        <div className="rounded-2xl p-5 border space-y-2" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <Row label="Name" value={me.full_name} />
          <Row label="Email" value={me.email} />
          <Row label="Age band" value={profile.age_band || '—'} />
          <Row label="Onboarding" value={profile.onboarding_completed ? 'Completed' : 'Not done'} />
          <Row label="Interests" value={(profile.interests || []).join(', ') || '—'} />
          <Row label="Hobbies" value={(profile.hobbies || []).join(', ') || '—'} />
          <Row label="Learning Style" value={profile.learning_style || '—'} />
        </div>
      </section>

      {/* Exam Prep — target exam. Set once here; Question Bank and quiz
          creation read it instead of asking again per page. */}
      <section data-testid="settings-target-exam">
        <h2 className="text-xl font-semibold">Exam prep</h2><AccentRule className="mt-2 mb-4" />
        <div className="rounded-2xl p-5 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <p className="text-sm font-semibold mb-1">Which exam are you preparing for?</p>
          <p className="text-xs opacity-70 mb-3">Used for Question Bank and tests you create yourself.</p>
          <div className="rounded-xl border overflow-hidden flex" style={{ borderColor: 'var(--st-border)' }}>
            {EXAM_OPTIONS.map((opt, i) => {
              const active = targetExam === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`target-exam-${opt.value}`}
                  disabled={saving}
                  aria-pressed={active}
                  onClick={() => setTargetExam(cur => nextTargetExam(cur, opt.value))}
                  className={`flex-1 px-3 py-3.5 text-sm font-semibold text-center transition disabled:opacity-60 ${i > 0 ? 'border-l' : ''}`}
                  style={{
                    borderColor: 'var(--st-border)',
                    background: active ? examGoldGradient : 'transparent',
                    color: active ? '#fff' : 'var(--st-ink)',
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Privacy & Personalisation (commented out) — DPDP §6 user control. See note at the top.
      <section data-testid="settings-privacy">
        <h2 className="text-xl font-semibold">Privacy and personalisation</h2><AccentRule className="mt-2 mb-4" />
        <div className="rounded-2xl p-5 border space-y-4" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--st-bg)' }}>
              <Brain className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold">Educational personalisation</p>
                <button
                  data-testid="personalisation-toggle"
                  onClick={() => togglePersonalisation(!personalisation)}
                  disabled={personalisationLoading}
                  role="switch"
                  aria-checked={personalisation}
                  className="relative inline-flex items-center w-11 h-6 rounded-full transition-colors flex-shrink-0"
                  style={{ background: personalisation ? 'var(--st-primary)' : '#CBD5E1' }}
                >
                  <span className="inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform"
                        style={{ transform: personalisation ? 'translateX(22px)' : 'translateX(2px)' }} />
                </button>
              </div>
              <p className="text-xs mt-1 opacity-70 leading-relaxed">
                When ON, Deskey ranks your next-lesson suggestions and tunes Magic Desk examples using your learning signals
                (quiz scores, dwell time, completion). When OFF, lessons are shown chronologically.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <ShieldCheck className="w-3 h-3"/> No ads
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  No profiling
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  No third-party sharing
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  DPDP §10 compliant
                </span>
              </div>
            </div>
          </div>
          <p className="text-xs opacity-60 leading-relaxed border-t pt-3" style={{ borderColor: 'var(--st-border)' }}>
            Your learning signals stay within your account and are used only to make Deskey lessons more relevant to you.
            You can request a full data export or erasure under DPDP §11–14 at <a className="underline" href="mailto:dpo@deskey.app">dpo@deskey.app</a>.
          </p>
        </div>
      </section>
      */}

      {/* Theme picker — 5 tier tabs */}
      <section data-testid="settings-theme">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div><h2 className="text-xl font-semibold">Theme</h2><AccentRule className="mt-2" /></div>
          <p className="text-xs opacity-60">Recommended for you: <b className="capitalize">{initialTier}</b></p>
        </div>

        <div className="flex flex-wrap p-1 rounded-2xl border mb-4" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} role="tablist">
          {THEME_TIERS.map(tt => {
            const active = tierTab === tt.id;
            return (
              <button key={tt.id} onClick={() => setTierTab(tt.id)} data-testid={`theme-tier-${tt.id}`}
                className="px-4 py-2 rounded-xl text-xs font-semibold transition flex-1 sm:flex-initial"
                style={{ background: active ? 'var(--st-primary)' : 'transparent', color: active ? 'var(--st-on-primary)' : 'inherit' }}>
                {tt.label}
                <span className="ml-1.5 opacity-60 font-normal">({themesByTier(tt.id).length})</span>
              </button>
            );
          })}
        </div>
        <p className="text-xs mb-4 opacity-60">{THEME_TIERS.find(x => x.id === tierTab)?.desc}</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {themesByTier(tierTab).map(th => {
            const active = themeId === th.id;
            return (
              <button key={th.id} onClick={() => setThemeId(th.id)} data-testid={`theme-pick-${th.id}`}
                className="relative rounded-2xl p-4 border text-left transition overflow-hidden"
                style={{
                  borderColor: active ? th.primary : th.border,
                  background: th.bg,
                  color: th.ink,
                  boxShadow: active ? `0 0 0 1px ${th.primary}` : 'none',
                  fontFamily: `"${th.headingFont}", ui-sans-serif`,
                }}>
                <div className="flex gap-1.5 mb-3">
                  <span className="w-6 h-6 rounded-md" style={{ background: th.primary }} />
                  <span className="w-6 h-6 rounded-md" style={{ background: th.accent }} />
                  <span className="w-6 h-6 rounded-md border" style={{ background: th.card, borderColor: th.border }} />
                </div>
                <div className="text-sm font-semibold">{th.name}</div>
                <div className="text-xs opacity-60 mt-0.5 capitalize">{th.vibe} · {th.headingFont}</div>
                {active && <div className="absolute top-3 right-3 w-5 h-5 rounded-full text-white flex items-center justify-center" style={{ background: th.primary }}><Check className="w-3 h-3" /></div>}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl p-4 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <label className="text-sm font-medium">Your own accent colour</label>
          <input type="color" value={accent || t.primary} onChange={e => setAccent(e.target.value)} className="w-12 h-9 rounded cursor-pointer" data-testid="accent-color" />
          {accent && <button onClick={() => setAccent('')} className="text-xs opacity-60 hover:opacity-100">Reset</button>}
          <span className="text-xs opacity-50 font-mono">{accent || t.primary}</span>
        </div>
      </section>

      {/* Typography */}
      <section data-testid="settings-typography">
        <h2 className="text-xl font-semibold">Typography</h2><AccentRule className="mt-2 mb-4" />
        <div className="space-y-4 rounded-2xl p-5 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          {/* Font size hidden for now — uncomment to bring the picker back.
          <div>
            <label className="text-sm font-medium block mb-2">Font size</label>
            <div className="inline-flex p-1 rounded-xl border" style={{ borderColor: 'var(--st-border)' }}>
              {FONT_SCALES.map(s => {
                const active = fontScale === s.id;
                return (
                  <button key={s.id} onClick={() => setFontScale(s.id)} data-testid={`font-scale-${s.id}`}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition"
                    style={{ background: active ? 'var(--st-primary)' : 'transparent', color: active ? 'var(--st-on-primary)' : 'inherit' }}>
                    {s.label}
                    <span className="ml-1 opacity-60 font-normal text-xs">{Math.round(s.multiplier * 100)}%</span>
                  </button>
                );
              })}
            </div>
          </div> */}

          <div>
            <label className="text-sm font-medium block mb-2">Font family</label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFontOverride('')} data-testid="font-override-default"
                className="px-3 py-2 rounded-xl text-xs font-semibold border transition"
                style={{
                  borderColor: !fontOverride ? 'var(--st-primary)' : 'var(--st-border)',
                  background: !fontOverride ? 'var(--st-primary)' : 'transparent',
                  color: !fontOverride ? 'var(--st-on-primary)' : 'inherit',
                }}>
                Match theme
                <span className="ml-1.5 opacity-60 font-normal text-xs">({t.headingFont})</span>
              </button>
              {FONT_OPTIONS.map(f => {
                const active = fontOverride === f.id;
                return (
                  <button key={f.id} onClick={() => setFontOverride(f.id)} data-testid={`font-override-${f.id.replace(/\s+/g, '-')}`}
                    className="px-3 py-2 rounded-xl text-xs font-semibold border transition"
                    style={{
                      borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                      background: active ? 'var(--st-primary)' : 'transparent',
                      color: active ? 'var(--st-on-primary)' : 'inherit',
                      fontFamily: `"${f.id}", ui-sans-serif, serif, monospace`,
                    }}>
                    {f.label}
                    <span className="ml-1.5 opacity-60 font-normal text-xs capitalize">({f.tone})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-xl p-4 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }} data-testid="font-preview">
            <p style={{ fontFamily: `"${fontOverride || t.headingFont}", ui-sans-serif`, fontSize: `${28 * (FONT_SCALES.find(s => s.id === fontScale)?.multiplier || 1)}px`, fontWeight: 600, lineHeight: 1.15 }}>
              The quick brown fox jumps over the lazy dog
            </p>
            <p className="opacity-70 mt-2" style={{ fontFamily: `"${fontOverride || t.bodyFont}", ui-sans-serif`, fontSize: `${14 * (FONT_SCALES.find(s => s.id === fontScale)?.multiplier || 1)}px` }}>
              Pack my box with five dozen liquor jugs. 1234567890
            </p>
          </div>
        </div>
      </section>

      {/* Stickers — pack tabs */}
      <section data-testid="settings-stickers">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div><h2 className="text-xl font-semibold">Stickers</h2><AccentRule className="mt-2" /></div>
          <p className="text-xs opacity-60">{stickers.length}/50 selected · drag &amp; place anywhere from the dashboard <b>Customize</b> button</p>
        </div>

        <div className="space-y-3 mb-4">
          {STICKER_TIER_GROUPS.map(tt => {
            const packs = packsByTier(tt.id);
            if (packs.length === 0) return null;
            return (
              <div key={tt.id}>
                <div className="text-xs font-semibold opacity-50 mb-1.5">{tt.label} packs</div>
                <div className="flex flex-wrap gap-1.5">
                  {packs.map(p => {
                    const active = packTab === p.id;
                    return (
                      <button key={p.id} onClick={() => setPackTab(p.id)} data-testid={`sticker-pack-${p.id}`}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold border transition"
                        style={{
                          borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                          background: active ? 'var(--st-primary)' : 'var(--st-card)',
                          color: active ? 'var(--st-on-primary)' : 'inherit',
                        }}>
                        {p.label}
                        <span className="ml-1 opacity-60 font-normal">{p.stickers.length}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div>
            <div className="text-xs font-semibold opacity-50 mb-1.5">My uploads</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setPackTab('__custom__')} data-testid="sticker-pack-custom"
                className="px-3 py-1.5 rounded-full text-xs font-semibold border transition"
                style={{
                  borderColor: packTab === '__custom__' ? 'var(--st-primary)' : 'var(--st-border)',
                  background: packTab === '__custom__' ? 'var(--st-primary)' : 'var(--st-card)',
                  color: packTab === '__custom__' ? 'var(--st-on-primary)' : 'inherit',
                }}>
                Custom <span className="ml-1 opacity-60 font-normal">{customStickers.length}</span>
              </button>
            </div>
          </div>
        </div>

        {packTab === '__custom__' ? (
          <CustomStickerGrid
            stickers={customStickers}
            selected={stickers}
            onToggle={toggleSticker}
            onDelete={deleteCustom}
            onUploadClick={() => fileRef.current?.click()}
            uploading={uploading}
          />
        ) : activePack ? (
          <BuiltinStickerGrid pack={activePack} selected={stickers} onToggle={toggleSticker} />
        ) : null}

        <input ref={fileRef} type="file" accept="image/png,image/webp,image/jpeg,image/gif" onChange={handleUpload} className="hidden" data-testid="sticker-upload-input" />
      </section>

      <div className="fixed bottom-0 left-0 right-0 backdrop-blur border-t z-30" style={{ background: 'color-mix(in srgb, var(--st-card) 90%, transparent)', borderColor: 'var(--st-border)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
          <div className="text-xs opacity-70">
            <b>{stickers.length}</b> stickers · <b>{t.name}</b> · <b>{fontOverride || t.headingFont}</b>
          </div>
          <Button onClick={save} disabled={saving || !hasChanges} className="rounded-full px-8 font-medium shadow-none" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }} data-testid="settings-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span style={{ color: 'var(--st-mute)' }}>{label}</span>
      <span className="font-medium text-right truncate max-w-[60%]">{value}</span>
    </div>
  );
}

function BuiltinStickerGrid({ pack, selected, onToggle }) {
  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid={`sticker-grid-${pack.id}`}>
      <div className="text-sm font-medium mb-3">{pack.label} <span className="tabular-nums" style={{ color: 'var(--st-mute)' }}>{pack.stickers.length}</span></div>
      <div className="flex flex-wrap gap-2">
        {pack.stickers.map(s => {
          const active = selected.includes(s.id);
          const tw = emojiToTwemojiUrl(s.emoji);
          return (
            <button key={s.id} onClick={() => onToggle(s.id)} title={s.label} data-testid={`sticker-pick-${s.id}`}
              className="w-14 h-14 rounded-2xl flex items-center justify-center border transition"
              style={{
                borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                background: active ? 'var(--st-primary)' : 'var(--st-card)',
              }}>
              <StickerImgOrGlyph url={tw} glyph={s.emoji} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Fallback-aware sticker renderer for the settings + onboarding grids.
function StickerImgOrGlyph({ url, glyph, size = 36 }) {
  const [failed, setFailed] = React.useState(false);
  if (failed || !url) return <span className="text-2xl">{glyph}</span>;
  return <img src={url} alt="" style={{ width: size, height: size, objectFit: 'contain' }} onError={() => setFailed(true)} />;
}

function CustomStickerGrid({ stickers, selected, onToggle, onDelete, onUploadClick, uploading }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="sticker-grid-custom">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium">Your stickers <span className="tabular-nums" style={{ color: 'var(--st-mute)' }}>{stickers.length}/50</span></div>
        <Button onClick={onUploadClick} disabled={uploading} className="rounded-full text-sm font-medium shadow-none" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }} data-testid="sticker-upload-btn">
          {uploading ? 'Uploading…' : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload PNG / GIF</>}
        </Button>
      </div>
      {stickers.length === 0 ? (
        <button onClick={onUploadClick}
          className="w-full rounded-2xl border border-dashed py-10 text-sm opacity-70 hover:opacity-100 transition flex flex-col items-center gap-2"
          style={{ borderColor: 'var(--st-border)' }}
          data-testid="sticker-upload-empty">
          <Plus className="w-6 h-6" />
          <span>Upload your first sticker</span>
          <span className="text-xs opacity-60">PNG · WEBP · JPEG · GIF · max 2 MB</span>
        </button>
      ) : (
        <div className="flex flex-wrap gap-2">
          {stickers.map(s => {
            const active = selected.includes(s.id);
            return (
              <div key={s.id} className="relative group" data-testid={`custom-sticker-${s.id}`}>
                <button onClick={() => onToggle(s.id)} title={s.label}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center border transition overflow-hidden"
                  style={{
                    borderColor: active ? 'var(--st-primary)' : 'var(--st-border)',
                    boxShadow: active ? '0 0 0 1px var(--st-primary)' : 'none',
                    background: 'var(--st-card)',
                  }}>
                  <img src={`${API}${s.url}`} alt={s.label} className="w-full h-full object-contain" />
                </button>
                <button onClick={() => onDelete(s.id)} title="Delete"
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  data-testid={`custom-sticker-delete-${s.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          <button onClick={onUploadClick}
            className="w-16 h-16 rounded-2xl border border-dashed flex flex-col items-center justify-center text-xs opacity-60 hover:opacity-100"
            style={{ borderColor: 'var(--st-border)' }}
            data-testid="sticker-upload-card">
            <Plus className="w-4 h-4" />
            Upload
          </button>
        </div>
      )}
    </div>
  );
}
